import omit from 'lodash/omit';
import { useReducer, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import { INSURANCE_ELIGIBILITY_FIELDS_TYPES } from '../../constants/formFields';
import { INITIAL_INSURANCE_FORM_STATE } from '../../constants/insurance/initial-insurance-form-state';

import useValidatePatientInsuranceForm from './useValidatePatientInsuranceForm';
import useInsuranceRelationOptions from '../selectOptions/useInsuranceRelationOptions';

import {
  getInitialInsuranceFormValues,
  validateInsuranceFormFields,
} from '../../utils/manage-insurances/get-initial-insurance-form-values';

import {
  selectPatientInsurancesList,
  selectPatientInsurancesListIds,
} from '../../redux/manage-insurances/selectors/get-patient-insurances-list';

const {
  PRIMARY_SUBSCRIBER,
  HEALTH_PLAN,
  GROUP_NUMBER,
  MEMBER_ID,
  DO_NOT_HAVE_INSURANCE,
} = INSURANCE_ELIGIBILITY_FIELDS_TYPES;

const INITIAL_STATE = [];

const reducer = (state, action) => {
  switch (action.type) {
    case 'ADD': {
      return [
        ...state,
        {
          formState: {
            ...omit(INITIAL_INSURANCE_FORM_STATE, DO_NOT_HAVE_INSURANCE),
            [PRIMARY_SUBSCRIBER]: action.payload.isPrimarySubscriber,
          },
          formErrors: {},
        },
      ];
    }

    case 'REMOVE': {
      const { index } = action.payload;
      return state.filter((_, i) => i !== index);
    }

    case 'UPDATE': {
      const { index, ...rest } = action.payload;

      return state.map((item, idx) => {
        if (idx === index) {
          return {
            ...item,
            formState: { ...item.formState, ...rest },
          };
        }
        return item;
      });
    }

    case 'SET_ERRORS': {
      const { index, errors } = action.payload;

      return state.map((item, idx) => {
        if (idx === index) {
          return {
            ...item,
            formErrors: errors,
          };
        }
        return item;
      });
    }

    case 'CLEAR_ERROR': {
      const { index, fieldName } = action.payload;

      return state.map((item, idx) => {
        if (idx === index) {
          const { formErrors } = item;
          const { [fieldName]: removedError, ...restErrors } = formErrors;

          return {
            ...item,
            formErrors: restErrors,
          };
        }
        return item;
      });
    }

    case 'CLEAR_ALL_ERRORS': {
      const { index } = action.payload;

      return state.map((item, idx) => {
        if (idx === index) {
          return {
            ...item,
            formErrors: {},
          };
        }
        return item;
      });
    }

    default:
      return state;
  }
};

function useFirstNonEmptyArray(value) {
  const ref = useRef(null);

  if (ref.current === null && value.length > 0) {
    ref.current = value;
  }

  return ref.current;
}

const useAdditionalInsurance = ({ isPrimarySubscriberDefault } = {}) => {
  const additionalInsurancesList = useSelector(selectPatientInsurancesList);

  const additionalInsurancesListIds = useSelector(
    selectPatientInsurancesListIds,
  );

  // snapshot, used to understand where to show error message related to
  // additional insurances forms
  const frozenAdditionalInsurancesListIds = useFirstNonEmptyArray(
    additionalInsurancesListIds,
  );

  const init = useCallback(() => {
    if (!additionalInsurancesList.length) {
      return INITIAL_STATE;
    }

    const additionalInsurancesFormValues = additionalInsurancesList.reduce(
      (acc, item) => {
        const formValues = getInitialInsuranceFormValues({
          insurance: item,
          isAdditionalInsurance: true,
        });

        acc.push({ formState: formValues, formErrors: {} });

        return acc;
      },
      [],
    );

    return additionalInsurancesFormValues;
  }, [additionalInsurancesList]);

  const [additionalInsurances, dispatch] = useReducer(
    reducer,
    INITIAL_STATE,
    init,
  );

  const { hideInsuranceRelationFieldOrInfo } = useInsuranceRelationOptions();

  const { validateFormFields } = useValidatePatientInsuranceForm();

  const isAdditionalInsurancesFormsValid = useMemo(() => {
    return additionalInsurances.every(({ formState, formErrors }) => {
      const isFormStateValid = validateInsuranceFormFields({
        formState,
        hideInsuranceRelationFieldOrInfo,
      });

      const hasErrors = Object.values(formErrors).some(Boolean);

      return isFormStateValid && !hasErrors;
    });
  }, [additionalInsurances, hideInsuranceRelationFieldOrInfo]);

  const addAdditionalInsurance = useCallback(() => {
    dispatch({
      type: 'ADD',
      payload: { isPrimarySubscriber: isPrimarySubscriberDefault },
    });
  }, [isPrimarySubscriberDefault]);

  const deleteAdditionalInsurance = useCallback(
    index => () => {
      dispatch({ type: 'REMOVE', payload: { index } });
    },
    [],
  );

  const handleAdditionalInsurancePrimarySubscriberChange = useCallback(
    index => value => {
      dispatch({
        type: 'UPDATE',
        payload: { index, [PRIMARY_SUBSCRIBER]: value },
      });

      dispatch({
        type: 'CLEAR_ALL_ERRORS',
        payload: { index },
      });
    },
    [],
  );

  const handleAdditionalInsuranceHealthPlanChange = useCallback(
    index => plan => {
      dispatch({
        type: 'UPDATE',
        payload: {
          index,
          [HEALTH_PLAN]: plan,
          ...(!plan.id && {
            [MEMBER_ID]: '',
            [GROUP_NUMBER]: '',
          }),
        },
      });
    },
    [],
  );

  const handleAdditionalInsuranceFormChange = useCallback(
    index => value => {
      dispatch({
        type: 'UPDATE',
        payload: { index, ...value },
      });
    },
    [],
  );

  const handleAdditionalInsuranceChangeField = useCallback(
    index => fieldName => value => {
      const data = { [fieldName]: value };

      dispatch({
        type: 'UPDATE',
        payload: { index, ...data },
      });

      dispatch({
        type: 'CLEAR_ERROR',
        payload: { index, fieldName },
      });
    },
    [],
  );

  const validateAdditionalInsurancesFormFields = () => {
    let isValid = true;

    additionalInsurances.forEach(({ formState }, index) => {
      const { isValid: isFormValid, errors } = validateFormFields(formState);

      if (!isFormValid) {
        isValid = false;
        dispatch({
          type: 'SET_ERRORS',
          payload: { index, errors },
        });
      }
    });

    return { isValid };
  };

  return {
    addAdditionalInsurance,
    additionalInsurances,
    deleteAdditionalInsurance,
    frozenAdditionalInsurancesListIds,
    handleAdditionalInsuranceChangeField,
    handleAdditionalInsuranceFormChange,
    handleAdditionalInsuranceHealthPlanChange,
    handleAdditionalInsurancePrimarySubscriberChange,
    isAdditionalInsurancesFormsValid,
    validateAdditionalInsurancesFormFields,
  };
};

export default useAdditionalInsurance;



// WEBPACK FOOTER //
// ./src/hooks/patientInsurance/useAdditionalInsurance.js